import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import axios from 'axios';
import {
  Sparkles, Loader2, RefreshCw, ChevronRight, ChevronDown, Stethoscope, Pill,
  FlaskConical, Plus, Check, X, AlertTriangle, ShieldAlert, Zap, Heart, TrendingUp, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const stripHtml = (h) => (h || '').replace(/<[^>]*>/g, ' ').trim();

/**
 * Clinical AI Co-Pilot — auto-fires when chief complaints exist and surfaces
 * three lanes of suggestions (Diagnoses · Medications · Investigations) with
 * per-item Adopt buttons + per-lane "Adopt all".
 *
 * Hooks into the existing global registries `window.addMedicineFromSuggestion`
 * (set by PrescriptionSection) and `window.addInvestigationFromSuggestion`
 * (set by InvestigationsSection) so adopting an item lands it directly in
 * the doctor's Rx / test list with no manual re-entry.
 */
export default function ClinicalAICopilot() {
  const { consultation, saveDraft } = useConsultation();
  const [data, setData] = useState(null);
  const [loadingDx, setLoadingDx] = useState(false);
  const [loadingRx, setLoadingRx] = useState(false);
  const loading = loadingDx || loadingRx;
  const [open, setOpen] = useState(true);
  const [adoptedIds, setAdoptedIds] = useState(() => new Set());
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [lastTrigger, setLastTrigger] = useState('');
  const debounceRef = useRef(null);

  const cc = stripHtml(consultation?.sections?.chief_complaints?.content);
  const dx = stripHtml(consultation?.sections?.final_diagnosis?.content || consultation?.sections?.provisional_diagnosis?.content);
  const triggerKey = `${cc}|${dx}`;
  const sections = consultation?.sections || {};

  const buildContext = useCallback(() => {
    const past = stripHtml(sections.past_history?.content);
    const exam = stripHtml(sections.clinical_examination?.content);
    return [
      cc && `Chief Complaints: ${cc}`,
      dx && `Diagnosis: ${dx}`,
      past && `History: ${past}`,
      exam && `Examination: ${exam}`,
    ].filter(Boolean).join('\n');
  }, [cc, dx, sections.past_history?.content, sections.clinical_examination?.content]);

  const fetchBundle = useCallback(async () => {
    const ctx = buildContext();
    if (!ctx || ctx.length < 6) return;
    try {
      const vitals = sections.vitals || {};
      const payload = {
        text: ctx,
        suggestion_type: 'diagnosis',  // bundle ignores, but schema needs it
        patient_id: consultation?.patient_id,
        patient_age: consultation?.patient_age,
        patient_gender: consultation?.patient_gender,
        vitals: {
          bp_systolic: vitals.bp_systolic, bp_diastolic: vitals.bp_diastolic,
          pulse: vitals.pulse, spo2: vitals.spo2,
          temperature: vitals.temperature, rbs: vitals.rbs, bmi: vitals.bmi,
        },
      };
      // Two-wave parallel fetch — diagnosis + etiology (wave A) lands in
      // ~6-10s, medicines + tests (wave B) in ~7-12s. Render each lane as
      // soon as its wave resolves so the doctor sees the differential
      // almost immediately instead of waiting for the full bundle.
      setData({ diagnosis: [], medicine: [], test: [], etiology: '', etiology_detail: null });
      setAdoptedIds(new Set());
      setLoadingDx(true);
      setLoadingRx(true);

      const dxPromise = axios.post(`${API}/api/suggestions/bundle/dx`, payload, { withCredentials: true })
        .then(({ data: dx }) => {
          setData(prev => ({
            ...(prev || {}),
            diagnosis: dx.diagnosis || [],
            etiology: dx.etiology || '',
            etiology_detail: dx.etiology_detail || null,
          }));
        })
        .catch(() => {/* lane stays empty — surfaced as 'no suggestions' */})
        .finally(() => setLoadingDx(false));

      const rxPromise = axios.post(`${API}/api/suggestions/bundle/rx`, payload, { withCredentials: true })
        .then(({ data: rx }) => {
          setData(prev => ({
            ...(prev || {}),
            medicine: rx.medicine || [],
            test:     rx.test     || [],
          }));
        })
        .catch(() => {/* lane stays empty — surfaced as 'no suggestions' */})
        .finally(() => setLoadingRx(false));

      await Promise.allSettled([dxPromise, rxPromise]);
    } catch (e) {
      toast.error('Could not load AI suggestions');
      setLoadingDx(false);
      setLoadingRx(false);
    }
  }, [buildContext, consultation, sections.vitals]);

  // Auto-fire on chief-complaint change (debounced 400ms) — first time only;
  // doctor can manually refresh after. Tight debounce lets Yanthra start
  // generating while the doctor is still typing the last word, so the
  // first lane lands almost immediately after they pause.
  useEffect(() => {
    if (!cc || cc.length < 4) return;
    if (triggerKey === lastTrigger) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLastTrigger(triggerKey);
      fetchBundle();
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [triggerKey, cc, fetchBundle, lastTrigger]);

  const adoptDiagnosis = (d) => {
    const existing = stripHtml(sections.provisional_diagnosis?.content);
    const next = existing
      ? `${existing}<br/>${d.description}${d.code ? ` (${d.code})` : ''}`
      : `${d.description}${d.code ? ` (${d.code})` : ''}`;
    saveDraft('provisional_diagnosis', next);
    toast.success(`Added "${d.description}" to provisional diagnosis`);
  };
  const adoptMedicine = (m) => {
    const med = {
      name: m.name, dose: m.dosage, frequency: m.frequency,
      duration: m.duration, intake: m.intake || 'after_food',
    };
    if (window.addMedicineFromSuggestion) {
      window.addMedicineFromSuggestion(med);
      toast.success(`Added ${m.name} to prescription`);
    } else {
      toast.error('Prescription form not ready — open the Rx section first');
    }
  };
  const adoptInvestigation = (t) => {
    // Prefer the live form's registered handler (so the InvestigationsSection
    // UI updates instantly). Fall back to writing directly into the
    // consultation section so the test is still persisted even when the
    // Investigations tab isn't currently mounted.
    if (window.addInvestigationFromSuggestion) {
      window.addInvestigationFromSuggestion({ name: t.name, reason: t.reason, priority: t.priority });
      toast.success(`Ordered ${t.name}`);
      return;
    }
    try {
      const inv = sections.investigations || {};
      const existing = Array.isArray(inv.items) ? inv.items : [];
      const next = [...existing, {
        id: Date.now(),
        name: t.name,
        priority: t.priority || 'routine',
        reason: t.reason || '',
      }];
      saveDraft('investigations', { items: next });
      toast.success(`Ordered ${t.name} — open Investigations tab to refine`);
    } catch (_) {
      toast.error('Could not add investigation');
    }
  };

  const handleAdopt = (kind, item, idx) => {
    const id = `${kind}-${idx}-${item.name || item.description}`;
    if (kind === 'diagnosis') adoptDiagnosis(item);
    else if (kind === 'medicine') adoptMedicine(item);
    else if (kind === 'test') adoptInvestigation(item);
    setAdoptedIds(prev => new Set([...prev, id]));
  };

  const handleDismiss = (kind, idx, item) => {
    const id = `${kind}-${idx}-${item.name || item.description}`;
    setDismissedIds(prev => new Set([...prev, id]));
  };

  const adoptAll = (kind) => {
    const arr = data?.[kind] || [];
    arr.forEach((it, i) => {
      const id = `${kind}-${i}-${it.name || it.description}`;
      if (!adoptedIds.has(id) && !dismissedIds.has(id) && !it.error) handleAdopt(kind, it, i);
    });
  };

  // ── Master actions ─────────────────────────────────────────────
  const adoptEverything = () => {
    ['diagnosis', 'medicine', 'test'].forEach(k => adoptAll(k));
    toast.success('All Yanthra suggestions adopted into the visit');
  };
  const startFresh = () => {
    setAdoptedIds(new Set());
    setDismissedIds(new Set());
    setData(null);
    setLastTrigger('');
    toast.success('Yanthra suggestions cleared — write your own note');
  };

  if (!cc) return null;
  const total = (data?.diagnosis?.length || 0) + (data?.medicine?.length || 0) + (data?.test?.length || 0);

  return (
    <div className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-4 mb-3 shadow-sm" data-testid="ai-copilot-card">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="ai-copilot-toggle"
      >
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white flex items-center justify-center shadow-md">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
            Yanthra AI Suggestions
            {loading && <Loader2 className="h-3 w-3 ml-2 animate-spin inline text-violet-600" />}
          </div>
          <div className="text-[11px] text-zinc-500">
            {loadingDx ? (
              'Thinking through the differential diagnosis…'
            ) : loadingRx ? (
              <>Diagnosis ready · loading medications + investigations…</>
            ) : data && total > 0 ? (
              <>Suggested <span className="font-semibold text-violet-700">{total}</span> items — explains likely cause, recommends Rx and investigations. Doctor remains in charge.</>
            ) : (
              'Will analyse chief complaints + vitals → likely cause, provisional diagnoses, Rx and investigations.'
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); fetchBundle(); }}
          className="text-[10px] font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-violet-100"
          title="Refresh AI suggestions"
          data-testid="ai-copilot-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && data && (
        <>
          <ReasoningBanner data={data} />
          {(data.etiology_detail || data.etiology) && (
            <LikelyCauseCard detail={data.etiology_detail} fallback={data.etiology} />
          )}
          {/* Master action bar — Adopt All · Start Fresh */}
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-violet-200 bg-white/70 px-3 py-2" data-testid="ai-master-actions">
            <div className="text-[11px] text-zinc-600">
              Pick individual items below, or apply / clear them in one click:
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={startFresh}
                className="text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md px-2.5 py-1 hover:bg-zinc-50"
                data-testid="ai-start-fresh"
              >
                Start fresh
              </button>
              <button
                onClick={adoptEverything}
                className="text-[11px] font-semibold text-white bg-gradient-to-br from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 rounded-md px-3 py-1 shadow-sm"
                data-testid="ai-adopt-all"
              >
                Adopt all suggestions
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
            <Lane
            kind="diagnosis"
            title="Possible diagnoses"
            icon={<Stethoscope className="h-3.5 w-3.5" />}
            color="violet"
            items={data.diagnosis || []}
            loading={loadingDx}
            adopted={adoptedIds}
            dismissed={dismissedIds}
            onAdopt={handleAdopt}
            onDismiss={handleDismiss}
            onAdoptAll={() => adoptAll('diagnosis')}
            renderItem={(d) => (
              <div>
                <div className="flex items-start gap-1.5">
                  {d.red_flag && <ShieldAlert className="h-3 w-3 text-red-600 flex-shrink-0 mt-0.5" title="Red flag — life-threatening" />}
                  <div className="font-semibold text-zinc-900 text-[13px] leading-tight">{d.description}</div>
                </div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  {d.code && <span className="text-[9px] font-mono bg-zinc-100 px-1 py-0 rounded">{d.code}</span>}
                  {d.confidence && (
                    <span className={`text-[9px] uppercase tracking-wider px-1 py-0 rounded ${
                      d.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                      d.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-zinc-100 text-zinc-600'
                    }`}>{d.confidence}</span>
                  )}
                  {d.red_flag && (
                    <span className="text-[9px] uppercase tracking-wider px-1 py-0 rounded bg-red-100 text-red-700 font-bold">RED FLAG</span>
                  )}
                </div>
                {Array.isArray(d.key_features) && d.key_features.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {d.key_features.slice(0, 3).map((kf, j) => (
                      <span key={j} className="text-[10px] bg-violet-50 text-violet-700 border border-violet-100 rounded px-1.5 py-0.5">
                        ✓ {kf}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          />
          <Lane
            kind="medicine"
            title="Recommended Rx"
            icon={<Pill className="h-3.5 w-3.5" />}
            color="emerald"
            items={data.medicine || []}
            loading={loadingRx}
            adopted={adoptedIds}
            dismissed={dismissedIds}
            onAdopt={handleAdopt}
            onDismiss={handleDismiss}
            onAdoptAll={() => adoptAll('medicine')}
            renderItem={(m) => (
              <div>
                <div className="font-semibold text-zinc-900 text-[13px] leading-tight">{m.name}</div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  {m.dosage && <span className="text-[10px] text-zinc-700 font-semibold">{m.dosage}</span>}
                  {m.frequency && <span className="text-[10px] text-zinc-700 font-mono bg-zinc-100 px-1 py-0 rounded">{m.frequency}</span>}
                  {m.duration && <span className="text-[10px] text-zinc-500">× {m.duration}</span>}
                  {m.timing && <span className="text-[10px] text-zinc-500 italic">{m.timing}</span>}
                </div>
                {m.dose_note && (
                  <div className="mt-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                    <span className="font-bold uppercase tracking-wider text-[9px]">Dose tip</span>
                    <span>{m.dose_note}</span>
                  </div>
                )}
                {m.interaction_warning && (
                  <div className="mt-1 text-[10px] text-red-800 bg-red-50 border border-red-200 rounded px-1.5 py-1 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-px text-red-600" />
                    <span><b>Interaction:</b> {m.interaction_warning}</span>
                  </div>
                )}
              </div>
            )}
          />
          <Lane
            kind="test"
            title="Suggested investigations"
            icon={<FlaskConical className="h-3.5 w-3.5" />}
            color="blue"
            items={data.test || []}
            loading={loadingRx}
            adopted={adoptedIds}
            dismissed={dismissedIds}
            onAdopt={handleAdopt}
            onDismiss={handleDismiss}
            onAdoptAll={() => adoptAll('test')}
            renderItem={(t) => (
              <div>
                <div className="font-semibold text-zinc-900 text-[13px] leading-tight">{t.name}</div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  {t.priority && (
                    <span className={`text-[9px] uppercase tracking-wider px-1 py-0 rounded font-bold ${
                      t.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      t.priority === 'follow-up' ? 'bg-zinc-100 text-zinc-600' :
                      'bg-blue-100 text-blue-700'
                    }`}>{t.priority}</span>
                  )}
                  {t.grouped_under && (
                    <span className="text-[9.5px] text-violet-700 bg-violet-50 border border-violet-100 rounded px-1 py-0">
                      r/o {t.grouped_under}
                    </span>
                  )}
                </div>
                {t.reason && (
                  <div className="text-[10.5px] text-zinc-600 mt-0.5 leading-snug">{t.reason}</div>
                )}
              </div>
            )}
          />
        </div>
        </>
      )}
    </div>
  );
}

function ReasoningBanner({ data }) {
  const dx = data.diagnosis || [];
  const meds = data.medicine || [];
  const tests = data.test || [];
  const redFlags = dx.filter(d => d.red_flag);
  const urgentTests = tests.filter(t => t.priority === 'urgent');
  const interactions = meds.filter(m => m.interaction_warning && m.interaction_warning.trim().length > 0);
  const topDx = dx[0];

  return (
    <div className="mt-3 rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-3" data-testid="ai-reasoning-banner">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-violet-700 mb-1">
            Clinical Reasoning
          </div>
          {topDx ? (
            <div className="text-[13px] text-zinc-800 leading-snug">
              Working diagnosis: <b className="text-violet-800">{topDx.description}</b>
              {topDx.code && <span className="text-[10px] text-zinc-500 ml-1 font-mono">({topDx.code})</span>}
              {topDx.confidence && (
                <span className={`ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0 rounded ${
                  topDx.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                  topDx.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-zinc-100 text-zinc-600'
                }`}>{topDx.confidence}</span>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-zinc-500">Analysing presentation…</div>
          )}
          {topDx?.rationale && (
            <div className="text-[11px] text-zinc-600 italic mt-0.5 leading-snug">{topDx.rationale}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {redFlags.length > 0 && (
            <span className="text-[10px] font-bold bg-red-100 text-red-800 border border-red-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> {redFlags.length} red flag{redFlags.length > 1 ? 's' : ''}
            </span>
          )}
          {urgentTests.length > 0 && (
            <span className="text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {urgentTests.length} urgent test{urgentTests.length > 1 ? 's' : ''}
            </span>
          )}
          {interactions.length > 0 && (
            <span className="text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {interactions.length} Rx interaction{interactions.length > 1 ? 's' : ''}
            </span>
          )}
          <span className="text-[10px] font-bold bg-violet-100 text-violet-800 border border-violet-200 rounded-full px-2 py-0.5">
            {dx.length} dx · {meds.length} Rx · {tests.length} tests
          </span>
        </div>
      </div>
    </div>
  );
}

const COLOR_MAP = {
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  btn: 'hover:bg-violet-100' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', btn: 'hover:bg-emerald-100' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    btn: 'hover:bg-blue-100' },
};

function Lane({ kind, title, icon, color, items, adopted, dismissed, onAdopt, onDismiss, onAdoptAll, renderItem, loading }) {
  const c = COLOR_MAP[color];
  const visible = items.filter((it, i) => !dismissed.has(`${kind}-${i}-${it.name || it.description}`) && !it.error);
  return (
    <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-testid={`ai-lane-${kind}`}>
      <div className={`${c.bg} px-3 py-2 flex items-center gap-1.5`}>
        <span className={c.text}>{icon}</span>
        <span className={`text-[11px] uppercase tracking-wider font-bold ${c.text}`}>{title}</span>
        {loading && (
          <Loader2 className={`h-3 w-3 animate-spin ${c.text}`} data-testid={`ai-lane-${kind}-loading`} />
        )}
        <span className="ml-auto text-[10px] text-zinc-500">{visible.length}</span>
        {visible.length > 0 && (
          <button
            onClick={onAdoptAll}
            className={`text-[10px] font-semibold ${c.text} ${c.btn} px-1.5 py-0.5 rounded transition`}
            data-testid={`ai-adopt-all-${kind}`}
          >
            Adopt all
          </button>
        )}
      </div>
      {items.length === 0 ? (
        loading ? (
          <div className="text-xs text-zinc-500 italic px-3 py-3 text-center inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="h-3 w-3 animate-spin" /> Generating suggestions…
          </div>
        ) : (
          <div className="text-xs text-zinc-400 italic px-3 py-3 text-center">No suggestions yet.</div>
        )
      ) : (
        <ul className="divide-y divide-zinc-100 max-h-72 overflow-y-auto">
          {items.map((it, i) => {
            const id = `${kind}-${i}-${it.name || it.description}`;
            if (dismissed.has(id) || it.error) return null;
            const isAdopted = adopted.has(id);
            return (
              <li key={id} className="px-3 py-2 flex items-start gap-2 hover:bg-zinc-50/60 transition" data-testid={`ai-item-${kind}-${i}`}>
                <div className="flex-1 min-w-0">
                  {renderItem(it)}
                  {it.rationale && (
                    <div className="text-[10px] text-zinc-500 mt-1 italic leading-snug">{it.rationale}</div>
                  )}
                </div>
                {isAdopted ? (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                    <Check className="h-2.5 w-2.5" /> Adopted
                  </span>
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onAdopt(kind, it, i)}
                      className={`text-[10px] font-bold text-white ${
                        color === 'violet' ? 'bg-violet-600 hover:bg-violet-700' :
                        color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
                        'bg-blue-600 hover:bg-blue-700'
                      } rounded px-1.5 py-0.5 inline-flex items-center gap-0.5 transition`}
                      data-testid={`ai-adopt-${kind}-${i}`}
                    >
                      <Plus className="h-2.5 w-2.5" /> Adopt
                    </button>
                    <button
                      onClick={() => onDismiss(kind, i, it)}
                      className="text-zinc-400 hover:text-red-600 p-0.5 rounded"
                      title="Ignore"
                      data-testid={`ai-dismiss-${kind}-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


/**
 * LikelyCauseCard — illustrative multi-section explainer.
 *
 * Renders the rich `etiology_detail` payload from the bundle endpoint
 * (summary, mechanism, triggers, risk factors, natural course, red-flag
 * escalation rules). Falls back to the legacy one-line `fallback` string
 * if the structured payload is unavailable.
 */
function LikelyCauseCard({ detail, fallback }) {
  if (!detail && !fallback) return null;

  // Old payload — one-liner string only.
  if (!detail) {
    return (
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid="ai-cause-banner">
        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1 inline-flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Likely cause
        </div>
        <div className="text-[12.5px] text-amber-900 leading-snug">{fallback}</div>
      </div>
    );
  }

  const confBadge = {
    high:   'bg-emerald-100 text-emerald-800 border-emerald-200',
    medium: 'bg-amber-100  text-amber-800   border-amber-200',
    low:    'bg-zinc-100   text-zinc-700    border-zinc-200',
  }[(detail.confidence || '').toLowerCase()] || 'bg-zinc-100 text-zinc-700 border-zinc-200';

  return (
    <div
      className="mt-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50/40 to-white overflow-hidden shadow-sm"
      data-testid="ai-cause-banner"
    >
      {/* Hero strip */}
      <div className="relative px-4 pt-3 pb-2 border-b border-amber-200/60 bg-amber-100/30">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Likely cause &nbsp;·&nbsp; Yanthra explainer
          </div>
          {detail.confidence && (
            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${confBadge}`}>
              {detail.confidence} confidence
            </span>
          )}
        </div>
        {detail.summary && (
          <p
            className="text-[13px] font-medium text-amber-950 leading-snug"
            style={{ fontFamily: 'Manrope' }}
            data-testid="ai-cause-summary"
          >
            {detail.summary}
          </p>
        )}
      </div>

      {/* Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
        {detail.mechanism && (
          <CauseSection icon={Activity} tone="violet" label="How it happens (pathophysiology)" testid="ai-cause-mechanism">
            <p className="text-[12px] text-zinc-700 leading-snug">{detail.mechanism}</p>
          </CauseSection>
        )}

        {Array.isArray(detail.triggers) && detail.triggers.length > 0 && (
          <CauseSection icon={Zap} tone="orange" label="Triggers / aggravating factors" testid="ai-cause-triggers">
            <ul className="space-y-0.5">
              {detail.triggers.map((t, i) => (
                <li key={i} className="text-[12px] text-zinc-700 leading-snug pl-3 relative">
                  <span className="absolute left-0 top-1 h-1.5 w-1.5 rounded-full bg-orange-400" />
                  {t}
                </li>
              ))}
            </ul>
          </CauseSection>
        )}

        {Array.isArray(detail.risk_factors) && detail.risk_factors.length > 0 && (
          <CauseSection icon={Heart} tone="rose" label="Why this patient (risk factors)" testid="ai-cause-risk">
            <ul className="space-y-0.5">
              {detail.risk_factors.map((t, i) => (
                <li key={i} className="text-[12px] text-zinc-700 leading-snug pl-3 relative">
                  <span className="absolute left-0 top-1 h-1.5 w-1.5 rounded-full bg-rose-400" />
                  {t}
                </li>
              ))}
            </ul>
          </CauseSection>
        )}

        {detail.natural_course && (
          <CauseSection icon={TrendingUp} tone="blue" label="If untreated (natural course)" testid="ai-cause-course">
            <p className="text-[12px] text-zinc-700 leading-snug">{detail.natural_course}</p>
          </CauseSection>
        )}

        {Array.isArray(detail.escalate_if) && detail.escalate_if.length > 0 && (
          <div className="md:col-span-2 rounded-md bg-red-50 border border-red-200 px-3 py-2" data-testid="ai-cause-escalate">
            <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1 inline-flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Escalate / refer urgently if
            </div>
            <div className="flex flex-wrap gap-1.5">
              {detail.escalate_if.map((t, i) => (
                <span key={i} className="text-[11px] font-medium bg-white text-red-800 border border-red-200 rounded-full px-2 py-0.5">
                  ⚠ {t}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CauseSection({ icon: Icon, tone, label, children, testid }) {
  const tones = {
    violet: { bg: 'bg-violet-100',  fg: 'text-violet-700' },
    orange: { bg: 'bg-orange-100',  fg: 'text-orange-700' },
    rose:   { bg: 'bg-rose-100',    fg: 'text-rose-700'   },
    blue:   { bg: 'bg-blue-100',    fg: 'text-blue-700'   },
  }[tone] || { bg: 'bg-zinc-100', fg: 'text-zinc-700' };
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
        <span className={`h-4 w-4 rounded-md grid place-items-center ${tones.bg}`}>
          <Icon className={`h-2.5 w-2.5 ${tones.fg}`} />
        </span>
        <span className={tones.fg}>{label}</span>
      </div>
      {children}
    </div>
  );
}
